import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

interface MenuItem {
  label: string
  path?: string
  icon?: string
  children?: MenuItem[]
}

interface MenuProps {
  isCollapsed?: boolean
}

function Menu({ isCollapsed = false }: MenuProps) {
  const navigate = useNavigate()
  const location = useLocation()
  // 手動で開いたメニュー（アクティブな子がない場合でも開いたままにする）
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())
  // 手動で閉じたメニュー（アクティブな子があっても閉じたままにする）
  const [collapsedMenus, setCollapsedMenus] = useState<Set<string>>(new Set())

  const menuItems: MenuItem[] = [
    {
      label: 'ダッシュボード',
      path: '/dashboards',
      icon: 'bi-speedometer2',
    },
    {
      label: '案件管理',
      path: '/projects/active-list',
      icon: 'bi-briefcase',
    },
    {
      label: 'お問い合わせ管理',
      path: '/contact/active-list',
      icon: 'bi-envelope',
    },
    {
      label: '学校管理',
      icon: 'bi-building',
      children: [
        {
          label: '仮登録一覧',
          path: '/school/pre-register',
        },
        {
          label: '学校一覧',
          path: '/school/school-list',
        },
      ],
    },
    {
      label: '請求管理',
      path: '/billing/current',
      icon: 'bi-receipt',
    },
    {
      label: 'アカウント一覧',
      path: '/account',
      icon: 'bi-people',
    },
    {
      label: 'マイページ',
      path: '/account/my_account',
      icon: 'bi-person',
    },
    {
      label: 'マニュアル',
      path: '/manual',
      icon: 'bi-journal-text',
    }
  ]

  const isActive = (path?: string) => {
    if (!path) return false
    return location.pathname === path
  }

  // メインメニュークリック時の処理
  const handleMainClick = (item: MenuItem) => {
    if (item.path) {
      navigate(item.path)
    }
  }

  const hasActiveChild = (children?: MenuItem[]) => {
    if (!children) return false
    return children.some((child) => isActive(child.path))
  }

  // 折りたたみ時に開いているメニューを閉じる
  useEffect(() => {
    if (isCollapsed) {
      setExpandedMenus(new Set())
    }
  }, [isCollapsed])

  // アクティブな子がないメニューの「手動で閉じた記録」をクリア
  useEffect(() => {
    if (isCollapsed) {
      return
    }

    setCollapsedMenus((prevCollapsed) => {
      const newCollapsed = new Set(prevCollapsed)
      let changed = false
      
      menuItems.forEach((item) => {
        if (item.children && !hasActiveChild(item.children)) {
          if (newCollapsed.has(item.label)) {
            newCollapsed.delete(item.label)
            changed = true
          }
        }
      })
      
      return changed ? newCollapsed : prevCollapsed
    })
  }, [location.pathname, isCollapsed])

  // expandedMenusを更新：アクティブな子があるメニューのみ開く
  useEffect(() => {
    if (isCollapsed) {
      setExpandedMenus(new Set())
      return
    }

    setExpandedMenus((prevExpanded) => {
      const newExpanded = new Set<string>()
      
      // アクティブな子があるメニューのみを追加
      menuItems.forEach((item) => {
        if (item.children && hasActiveChild(item.children)) {
          // アクティブな子がある場合、手動で閉じていない限り開く
          if (!collapsedMenus.has(item.label)) {
            newExpanded.add(item.label)
          }
        }
        // アクティブな子がない場合は newExpanded に追加しない（閉じる）
      })
      
      // 変更がない場合は前の状態を返す（不要な再レンダリングを防ぐ）
      if (newExpanded.size === prevExpanded.size && 
          Array.from(newExpanded).every(label => prevExpanded.has(label))) {
        return prevExpanded
      }
      
      return newExpanded
    })
  }, [location.pathname, isCollapsed, collapsedMenus])

  // メニューが開いているかどうかを判定
  const isOpen = (label: string) => {
    // サイドバー縮小時は手動で開いたものだけを表示（アクティブ判定で自動展開しない）
    if (isCollapsed) {
      return expandedMenus.has(label)
    }

    const menuItem = menuItems.find((item) => item.label === label)
    
    if (!menuItem?.children) {
      return false
    }
    
    // 手動で閉じた場合は閉じたまま
    if (collapsedMenus.has(label)) {
      return false
    }
    
    // アクティブな子メニューがある場合は自動で開く
    if (hasActiveChild(menuItem.children)) {
      return true
    }
    
    // アクティブな子がない場合、手動で開いた場合のみ開く
    return expandedMenus.has(label)
  }

  // メニューの開閉を切り替え
  const toggleMenu = (label: string) => {
    const menuItem = menuItems.find((item) => item.label === label)
    const currentlyOpen = isOpen(label)

    // サイドバー縮小時は一度に一つだけ開く（重なり防止）
    if (isCollapsed) {
      setExpandedMenus((prev) => {
        const next = new Set(prev)
        if (next.has(label)) {
          next.delete(label)
        } else {
          next.clear()
          next.add(label)
        }
        return next
      })
      return
    }
    
    if (currentlyOpen) {
      // 閉じる場合
      if (menuItem?.children && hasActiveChild(menuItem.children)) {
        // アクティブな子がある場合、手動で閉じた記録を追加
        setCollapsedMenus((prev) => {
          const newSet = new Set(prev)
          newSet.add(label)
          return newSet
        })
      } else {
        // アクティブな子がない場合、expandedMenusから削除
        setExpandedMenus((prev) => {
          const newSet = new Set(prev)
          newSet.delete(label)
          return newSet
        })
      }
    } else {
      // 開く場合
      if (menuItem?.children && hasActiveChild(menuItem.children)) {
        // アクティブな子がある場合、手動で閉じた記録を削除
        setCollapsedMenus((prev) => {
          const newSet = new Set(prev)
          newSet.delete(label)
          return newSet
        })
      } else {
        // アクティブな子がない場合、expandedMenusに追加
        setExpandedMenus((prev) => {
          const newSet = new Set(prev)
          newSet.add(label)
          return newSet
        })
      }
    }
  }

  // サブメニューアイテムをクリックした時の処理
  const handleSubMenuClick = (_parentLabel: string, childPath: string) => {
    navigate(childPath)

    // サイドバー縮小時は遷移後にサブメニューを閉じる
    if (isCollapsed) {
      setExpandedMenus(new Set())
    }
    // サブメニューをクリックした時は親メニューを閉じない
    // （アクティブなページがあるので、useEffectで自動的に開かれる）
    // 手動で閉じた記録も追加しない
  }

  return (
    <nav className={`menu ${isCollapsed ? 'menu-collapsed' : ''}`}>
      <ul className="menu-list">
        {menuItems.map((item) => (
          <li key={item.label} className="menu-item">
            {item.children ? (
              <>
                <button
                  className={`menu-button menu-button-parent ${isOpen(item.label) ? 'open' : ''}`}
                  onClick={() => toggleMenu(item.label)}
                >
                  {item.icon && <i className={`bi ${item.icon}`}></i>}
                  <span className="menu-label">{item.label}</span>
                  <span className="menu-arrow">›</span>
                </button>
                {isOpen(item.label) && (
                  <ul className="menu-sublist">
                    {item.children.map((child) => (
                      <li key={child.path} className="menu-subitem">
                        <button
                          className={`menu-button menu-button-child ${isActive(child.path) ? 'active' : ''}`}
                          onClick={() => handleSubMenuClick(item.label, child.path!)}
                        >
                          <span className="menu-label">{child.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <button
                className={`menu-button ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleMainClick(item)}
              >
                {item.icon && <i className={`bi ${item.icon}`}></i>}
                <span className="menu-label">{item.label}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default Menu

